"use client";

import { Circle, GoogleMap, Marker } from "@react-google-maps/api";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import Colors from "ui-kit/colors";
import styles from "./VetMap.module.css";
import { parse, isWithinInterval, isValid } from "date-fns";

const defaultMapOptions = {
  fullscreenControl: false,
  mapTypeControl: false,
  zoomControl: true,
  tilt: 0,
  gestureHandling: "auto",
  mapTypeId: "roadmap",
  streetViewControl: false,
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.business",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.government",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.attraction",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.place_of_worship",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.school",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.sports_complex",
      stylers: [{ visibility: "off" }],
    },
  ],
};

export type MarkerType = {
  id: string;
  lat: number;
  lng: number;
  name: string;
  placeId: string;
  address: string;
  rating?: number;
  openNow?: boolean;
};

export type PlaceDetails = {
  id: string;
  placeId: string;
  name: string;
  address: string;
  phoneNumber?: string;
  website?: string;
  openingHours?: string[];
  openNow?: boolean;
  rating?: number;
};

// Circle styles for the three layers
const layerOptions = [
  {
    radius: 40,
    fillColor: Colors.blue300,
    fillOpacity: 0.2,
    strokeWeight: 0,
  },
  {
    radius: 60,
    fillColor: Colors.blue300,
    fillOpacity: 0.1,
    strokeWeight: 0,
  },
];

const isLocationOpenNow = (openingHours: { open: string; close: string }[]) => {
  const now = new Date();
  const currentDay = now.getDay(); // 0 is Sunday, 1 is Monday, etc.
  const swedishDay = currentDay === 0 ? 6 : currentDay - 1;

  const todayHours = openingHours[swedishDay];
  if (!todayHours) return false;

  const currentTime = now;

  if (todayHours.open === "00:00" && todayHours.close === "00:00") {
    return true;
  }

  if (todayHours.open === "Stängt" && todayHours.close === "Stängt") {
    return false;
  }

  if (todayHours.open === "Öppet dygnet runt" && !todayHours.close) {
    return true;
  }

  try {
    const openTime = parse(todayHours.open, "HH:mm", now);
    const closeTime = parse(todayHours.close, "HH:mm", now);
    
    if (isValid(openTime) && isValid(closeTime)) {
      return isWithinInterval(currentTime, { start: openTime, end: closeTime });
    }

    return false;
  } catch (error) {
    console.error("Error parsing opening hours:", error);
    return false;
  }
};

export type VetMapRef = {
  selectMarkerById: (id: string) => void;
};

type VetMapProps = {
  id?: string;
  onMarkerSelect?: (marker: MarkerType) => void;
  onMarkersChange: (markers: MarkerType[]) => void;
  onSelectPlace: (place: PlaceDetails) => void;
  predefinedLocations?: {
    id: string;
    lat: number;
    lng: number;
    name: string;
    address: string;
    placeId: string;
    openingHours: { day: string; open: string; close: string }[];
  }[];
};

const VetMap = forwardRef<VetMapRef, VetMapProps>(
  (
    { id, onSelectPlace, onMarkersChange, onMarkerSelect, predefinedLocations },
    ref
  ) => {
    const [zoom, setZoom] = useState(15);
    const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(
      undefined
    );
    const [markers, setMarkers] = useState<MarkerType[]>([]);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [selectedPlace, setSelectedPlace] = useState<PlaceDetails | null>(
      null
    );

    useEffect(() => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        () => {
          setCenter({ lat: 59.325, lng: 18.05 });
        }
      );
    }, []);

    const getPlaceDetails = useCallback(
      (id: string, placeId: string): Promise<PlaceDetails | null> => {
        if (!mapRef.current) return Promise.resolve(null);

        const service = new google.maps.places.PlacesService(mapRef.current);

        return new Promise((resolve) => {
          const request = {
            language: "sv",
            placeId,
            fields: [
              "name",
              "formatted_address",
              "formatted_phone_number",
              "website",
              "opening_hours",
              "rating",
            ],
          };

          service.getDetails(request, (place, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && place) {
              const placeDetails: PlaceDetails = {
                id,
                placeId,
                name: place.name || "",
                address: place.formatted_address || "",
                phoneNumber: place.formatted_phone_number || "",
                website: place.website || "",
                openingHours: place.opening_hours?.weekday_text || [],
                openNow: place.opening_hours?.open_now,
                rating: place.rating,
              };
              resolve(placeDetails);
            } else {
              resolve(null);
            }
          });
        });
      },
      []
    );

    const findNearestPlace = useCallback(
      (lat: number, lng: number, name: string) => {
        if (!mapRef.current) return null;

        const service = new google.maps.places.PlacesService(mapRef.current);
        const location = new google.maps.LatLng(lat, lng);

        const request = {
          location: location,
          radius: 100, // Search within 100 meters
          type: "veterinary_care",
          keyword: name,
        };

        return new Promise<string>((resolve, reject) => {
          service.nearbySearch(request, (results, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              results &&
              results.length > 0
            ) {
              resolve(results[0].place_id || "");
            } else {
              reject("No place found");
            }
          });
        });
      },
      []
    );

    const handleMarkerClick = useCallback(
      async (marker: MarkerType) => {
        try {
          // Try to find the corresponding Google Place
          const placeId = await findNearestPlace(
            marker.lat,
            marker.lng,
            marker.name
          );
          if (placeId) {
            const placeDetails = await getPlaceDetails(marker.id, placeId);
            setSelectedPlace(placeDetails);
          } else {
            // Fallback to basic details if no Google Place is found
            const basicDetails: PlaceDetails = {
              id: marker.id,
              placeId: marker.placeId || "",
              name: marker.name,
              address: marker.address,
            };
            setSelectedPlace(basicDetails);
          }
        } catch (error) {
          console.log("Failed to find place:", error);
          // Fallback to basic details
          const basicDetails: PlaceDetails = {
            id: marker.id,
            placeId: marker.placeId || "",
            name: marker.name,
            address: marker.address,
          };
          setSelectedPlace(basicDetails);
        }

        mapRef.current?.panTo({ lat: marker.lat, lng: marker.lng });
        mapRef.current?.setZoom(15);
      },
      [findNearestPlace, getPlaceDetails]
    );

    const onMapIdle = useCallback(() => {
      if (!mapRef.current || !predefinedLocations) return;

      const bounds = mapRef.current.getBounds();
      if (!bounds) return;

      const visibleMarkers: MarkerType[] = predefinedLocations
        .filter((location) => {
          return bounds.contains({ lat: location.lat, lng: location.lng });
        })
        .map((location, index) => ({
          ...location,
          openNow: location.openingHours
            ? isLocationOpenNow(location.openingHours)
            : undefined,
        }));

      setMarkers(visibleMarkers);
      onMarkersChange(visibleMarkers);
    }, [predefinedLocations, onMarkersChange]);

    const onMapLoaded = useCallback(
      (map: google.maps.Map) => {
        if (!center) return;

        mapRef.current = map;
      },
      [center]
    );

    useEffect(() => {
      if (selectedPlace) {
        onSelectPlace(selectedPlace);
      }
    }, [selectedPlace, onSelectPlace]);

    const selectMarkerById = useCallback(
      (id: string) => {
        const marker = markers.find((m) => m.id === id);
        if (marker && mapRef.current) {
          mapRef.current.panTo({ lat: marker.lat, lng: marker.lng });
          mapRef.current.setZoom(15);
          handleMarkerClick(marker);
          onMarkerSelect?.(marker);
          setMarkers([]);
        }
      },
      [markers, onMarkerSelect, handleMarkerClick]
    );

    // Expose the selectMarkerByPlaceId function to the parent component
    useImperativeHandle(ref, () => ({
      selectMarkerById,
    }));

    const customMarkerIcon = useMemo(
      () => ({
        path: google?.maps?.SymbolPath?.CIRCLE || 0,
        fillColor: Colors.blue300,
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: Colors.white,
        strokeOpacity: 1,
        scale: 8,
      }),
      []
    );

    const customSelectedMarkerIcon = useMemo(
      () => ({
        path: google?.maps?.SymbolPath?.CIRCLE || 0,
        fillColor: Colors.blue300,
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: Colors.blue300,
        strokeOpacity: 1,
        scale: 8,
      }),
      []
    );

    return (
      <div id={id} className={styles.mapContainerWrapper}>
        {center && (
          <GoogleMap
            mapContainerClassName={styles.mapContainer}
            center={center}
            options={defaultMapOptions}
            zoom={zoom}
            onZoomChanged={() => {
              setZoom(mapRef.current?.getZoom() || 15);
            }}
            onLoad={onMapLoaded}
            onIdle={onMapIdle}
          >
            {markers.map((marker, idx) => {
              const isSelected = selectedPlace?.id === marker.id;

              return (
                <React.Fragment key={idx}>
                  <Marker
                    position={{ lat: marker.lat, lng: marker.lng }}
                    title={marker.name}
                    onClick={() => {
                      handleMarkerClick(marker);
                    }}
                    icon={
                      isSelected ? customSelectedMarkerIcon : customMarkerIcon
                    }
                  />

                  {/* Render the circles if the marker is selected */}
                  {isSelected &&
                    layerOptions.map((options, index) => (
                      <Circle
                        key={index}
                        center={{ lat: marker.lat, lng: marker.lng }}
                        radius={options.radius}
                        options={{
                          fillColor: options.fillColor,
                          fillOpacity: options.fillOpacity,
                          strokeWeight: options.strokeWeight,
                        }}
                      />
                    ))}
                </React.Fragment>
              );
            })}
          </GoogleMap>
        )}
      </div>
    );
  }
);

VetMap.displayName = "VetMap";

export default VetMap;
